import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { addToWishlist, removeFromWishlist, getWishlist } from '../api/wishlist';
import { useTryonEnabled } from '../context/OrgContext';
import { canTryOn } from '../lib/tryon/frameUtils';
import TryonModal from './tryon/TryonModal';
import StarRating from './StarRating';

interface Props {
  product: Product;
  onWishlistChange?: () => void;
}

const ProductCard: React.FC<Props> = ({ product, onWishlistChange }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, openLogin } = useAuth();
  const [wishlisted, setWishlisted] = useState(!!product.is_wishlisted);
  const [adding, setAdding] = useState(false);
  const [tryonOpen, setTryonOpen] = useState(false);

  const tryonEnabled = useTryonEnabled();
  // Show try-on only when the org feature is on AND the product supports it.
  const canTryThisOn = tryonEnabled && canTryOn(product);

  const imageUrl = product.image_uri?.[0] || '/placeholder-glasses.png';
  const finalPrice = product.selling_price;
  const originalPrice = product.discount
    ? Math.round(product.selling_price / (1 - product.discount / 100))
    : null;

  const handleWishlist = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { openLogin(); return; }

    if (wishlisted) {
      const list = await getWishlist();
      const item = list.find((w) => w.product_id === product.id);
      if (item) {
        await removeFromWishlist(item.wishlist_id);
        setWishlisted(false);
      }
    } else {
      await addToWishlist(product.id);
      setWishlisted(true);
    }
    onWishlistChange?.();
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { openLogin(); return; }
    setAdding(true);
    await addToCart(product.id, 1);
    setAdding(false);
  };

  const isOutOfStock = product.quantity === 0;

  return (
    <div className="product-card" onClick={() => navigate(`/product/${product.id}`)}>
      <div className="product-card__image-wrap">
        <img src={imageUrl} alt={product.product_name} className="product-card__image" loading="lazy" />
        <button
          className={`product-card__wish-btn ${wishlisted ? 'active' : ''}`}
          onClick={handleWishlist}
          title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill={wishlisted ? '#ef4444' : 'none'} stroke={wishlisted ? '#ef4444' : '#374151'} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
        {(product.discount ?? 0) > 0 && !isOutOfStock ? (
          <span className="product-card__badge">{Math.round(product.discount!)}% OFF</span>
        ) : null}
        {canTryThisOn && !isOutOfStock && (
          <button
            className="product-card__try-me"
            onClick={(e) => { e.stopPropagation(); setTryonOpen(true); }}
            type="button"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="14" r="4" /><circle cx="18" cy="14" r="4" /><line x1="10" y1="13" x2="14" y2="13" />
            </svg>
            Try On
          </button>
        )}
        {isOutOfStock && (
          <div className="product-card__out-of-stock">Out of Stock</div>
        )}
      </div>

      <div className="product-card__info">
        {product.brand && String(product.brand).toLowerCase() !== 'nan' && (
          <p className="product-card__brand">{product.brand}</p>
        )}
        <h3 className="product-card__name">{product.product_name}</h3>
        {product.rating != null && product.rating > 0 && (
          <div style={{ margin: '4px 0' }}>
            <StarRating rating={product.rating} />
          </div>
        )}
        <div className="product-card__price-row">
          <span className="product-card__price">
            ₹{Math.round(finalPrice).toLocaleString()}
          </span>
          {originalPrice && (
            <span className="product-card__original-price">
              ₹{originalPrice.toLocaleString()}
            </span>
          )}
        </div>

        <button
          className="product-card__cart-btn"
          onClick={handleAddToCart}
          disabled={adding || isOutOfStock}
          style={isOutOfStock ? { background: '#9ca3af', cursor: 'not-allowed' } : undefined}
        >
          {isOutOfStock ? 'Out of Stock' : adding ? 'Adding...' : 'Add to Cart'}
        </button>
      </div>

      {/* Virtual Try-On modal — only mounted when the org feature is enabled and
          this product has a usable front-frame image. */}
      {canTryThisOn && (
        <TryonModal open={tryonOpen} onClose={() => setTryonOpen(false)} product={product} />
      )}
    </div>
  );
};

export default ProductCard;
